import api from '@Services/api';
import { HeadersResponse } from '@Services/generics/response';
import { AxiosResponse } from 'axios';
import {
	CRUDBase,
	DeleteSingleEntityPayload,
	GenerateCardMeta,
	GetAllMeta,
	GetSingleEntityPayload,
} from 'src/base/services/services.base';
import { ScratchCard } from '../Types/ScratchCards.types';

export class ScratchCardsRequester implements CRUDBase<ScratchCard> {
	private static instance: ScratchCardsRequester;
	static getInstance() {
		if (!ScratchCardsRequester.instance) {
			ScratchCardsRequester.instance = new ScratchCardsRequester();
		}
		return ScratchCardsRequester.instance;
	}
	baseURL = 'scratchcards/';
	getAll(
		payload?: GetAllMeta
	): Promise<AxiosResponse<ApiResponse<ScratchCard[]>>>;
	getAll(payload: number): Promise<AxiosResponse<ApiResponse<ScratchCard[]>>>;
	async getAll(
		payload?: any
	): Promise<AxiosResponse<ApiResponse<ScratchCard[]>>> {
		return await api.post(`${this.baseURL}all`, stripEmptySortBy(payload));
	}
	get(id: string): Promise<AxiosResponse<ApiResponse<ScratchCard>>>;
	get(id: number): Promise<AxiosResponse<ApiResponse<ScratchCard>>>;
	get(
		id: string | number,
		payload: GetSingleEntityPayload
	): Promise<AxiosResponse<ApiResponse<ScratchCard>>>;
	get(
		id: any,
		payload?: any
	): Promise<import('axios').AxiosResponse<ApiResponse<ScratchCard>>> {
		throw new Error('Method not implemented.');
	}
	create(
		payload: Omit<ScratchCard, 'id'>
	): Promise<AxiosResponse<ApiResponse<ScratchCard>>> {
		throw new Error('Method not implemented.');
	}
	/**
	 * Retarget one unused card — `{ id, <exactly one target>, value? }`.
	 * The backend replaces the whole target set and 409s on a used card.
	 */
	async update(
		payload: ScratchCard
	): Promise<AxiosResponse<ApiResponse<ScratchCard>>> {
		return await api.put(`${this.baseURL}update`, payload);
	}
	delete(id: number): Promise<AxiosResponse<ApiResponse<any>>>;
	delete(id: string): Promise<AxiosResponse<ApiResponse<any>>>;
	delete(
		id: string | number,
		payload: DeleteSingleEntityPayload
	): Promise<AxiosResponse<ApiResponse<any>>>;
	async delete(
		id: any,
		payload?: any
	): Promise<import('axios').AxiosResponse<ApiResponse<any>>> {
		return await api.delete(`${this.baseURL}delete`, { data: { id } });
	}
	async export(payload: GetAllMeta): Promise<AxiosResponse<string>> {
		return await api.post(`${this.baseURL}all`, {
			...stripEmptySortBy(payload),
			export: true,
		});
	}
	async generate?(payload: GenerateCardMeta): Promise<AxiosResponse<string>> {
		return await api.post(`${this.baseURL}generate`, payload);
	}
}

/**
 * The backend's GetAll validator requires `sortBy.field` once the key is
 * present, so a bare `sortBy: {}` (the slice's initial state) is a 422.
 * Stripped here so every caller — table, modals, refetches — is covered.
 */
export const stripEmptySortBy = (payload?: any) => {
	if (payload?.sortBy && !payload.sortBy.field) {
		const { sortBy, ...rest } = payload;
		return rest;
	}
	return payload;
};

/** Un-uses a redeemed classroom-course card (backend `POST scratchcards/reset`). */
export const resetScratchcard = (payload: { id: number; user_id: number }) =>
	api.post('scratchcards/reset', payload);

/** Retargets every unused card in a batch; responds `{ updated, skipped_used }`. */
export const updateScratchcardsByBatch = (payload: Record<string, any>) =>
	api.put('scratchcards/update/by-batch', payload);
